import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  Home,
  Wrench,
  Rocket,
  Building2,
  Check,
  Phone,
  Zap,
  Sun,
  BatteryCharging,
} from 'lucide-react';
import CertificationsSection from '../../components/Certifications';
import { useT } from '../../i18n/LanguageContext';

const heroImg =
  'https://images.unsplash.com/photo-1634586648651-f1fb9ec10d90?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwzfHxidWlsZGluZyUyMHJlbm92YXRpb258ZW58MHx8fHwxNzg0ODE2ODk4fDA&ixlib=rb-4.1.0&q=85';

const content = {
  fr: {
    back: 'Tous nos services',
    badge: 'SERVICE · RÉNOVATION ÉNERGÉTIQUE',
    title: 'Rénovation',
    accent: 'énergétique.',
    hero:
      'Isolation thermique, aménagement des combles et solutions d’efficacité énergétique pour réduire vos consommations et valoriser durablement votre bâtiment.',
    home: 'Accueil',
    services: 'Services',
    current: 'Rénovation énergétique',

    expertise: 'NOTRE EXPERTISE',
    expertiseTitle: 'Ce que nous réalisons pour vous',

    description:
      'GPE vous accompagne dans vos projets de rénovation énergétique en mettant à votre disposition un conseiller attitré qui vous suivra pendant toute la durée du projet. De l’étude à la réalisation, nous prenons en charge l’isolation thermique, l’aménagement des combles et les solutions d’efficacité énergétique adaptées à votre bâtiment pour un confort accru et des factures maîtrisées.',

    methodologyTitle: 'Méthodologie & accompagnement',
    methodology: [
      'Études, conception et dimensionnement',
      'Audit énergétique et diagnostic préalable',
      'Accompagnement, assistance et conseils d’un conseiller attitré',
      'Suivi de projet du début à la fin des travaux',
    ],

    solutionsTitle: 'Nos solutions',
    solutions: [
      'Isolation thermique par l’intérieur (ITI)',
      'Isolation thermique par l’extérieur (ITE)',
      'Isolation des combles perdus et aménagés',
      'Isolation du plancher bas',
      'Aménagement des combles',
      'Remplacement des menuiseries et systèmes de chauffage',
    ],

    sectorsTitle: 'Secteurs d’intervention',
    sectors: [
      'Logements / Habitat',
      'Tertiaire / Bureaux',
      'Copropriétés',
      'Collectivités / Établissements publics',
    ],

    ctaTitle: 'Un projet de rénovation ?',
    ctaText:
      'Un conseiller attitré vous accompagne du diagnostic à la réception des travaux.',
    quote: 'Demander un devis',
    call: 'Nous appeler',

    relatedTitle: 'Services complémentaires',
    related: [
      {
        slug: 'courants-forts',
        title: 'Courants forts',
        desc: 'Installations électriques BT/HT tertiaires et industrielles.',
        icon: Zap,
      },
      {
        slug: 'energies-renouvelables',
        title: 'Énergies renouvelables',
        desc: 'Solutions photovoltaïques clé en main.',
        icon: Sun,
      },
      {
        slug: 'mobilite-electrique',
        title: 'Mobilité électrique',
        desc: 'Déploiement d’infrastructures de recharge IRVE.',
        icon: BatteryCharging,
      },
    ],
  },

  en: {
    back: 'All our services',
    badge: 'SERVICE · ENERGY RENOVATION',
    title: 'Energy',
    accent: 'renovation.',
    hero:
      'Thermal insulation, loft conversion and energy-efficiency solutions designed to reduce consumption and sustainably enhance your building.',
    home: 'Home',
    services: 'Services',
    current: 'Energy renovation',

    expertise: 'OUR EXPERTISE',
    expertiseTitle: 'What we deliver for you',

    description:
      'GPE supports you throughout your energy renovation projects by providing a dedicated advisor who follows your project from start to finish. From the initial assessment through completion, we manage thermal insulation, loft improvements and energy-efficiency solutions adapted to your building, helping improve comfort while keeping energy bills under control.',

    methodologyTitle: 'Methodology & support',
    methodology: [
      'Studies, design and sizing',
      'Energy audit and preliminary assessment',
      'Personalised support, assistance and advice from a dedicated advisor',
      'Project monitoring from the beginning to completion of the works',
    ],

    solutionsTitle: 'Our solutions',
    solutions: [
      'Internal thermal insulation (ITI)',
      'External thermal insulation (ITE)',
      'Insulation of unused and converted lofts',
      'Ground-floor insulation',
      'Loft conversion',
      'Replacement of windows, doors and heating systems',
    ],

    sectorsTitle: 'Industries we serve',
    sectors: [
      'Residential / Housing',
      'Commercial / Offices',
      'Residential co-ownerships',
      'Public authorities / Public facilities',
    ],

    ctaTitle: 'Have a renovation project?',
    ctaText:
      'A dedicated advisor will support you from the initial assessment through project completion.',
    quote: 'Request a quotation',
    call: 'Call us',

    relatedTitle: 'Related services',
    related: [
      {
        slug: 'courants-forts',
        title: 'Power systems',
        desc: 'Commercial and industrial LV/MV electrical installations.',
        icon: Zap,
      },
      {
        slug: 'energies-renouvelables',
        title: 'Renewable energy',
        desc: 'Turnkey photovoltaic solutions.',
        icon: Sun,
      },
      {
        slug: 'mobilite-electrique',
        title: 'Electric mobility',
        desc: 'EV charging infrastructure deployment.',
        icon: BatteryCharging,
      },
    ],
  },

  ar: {
    back: 'جميع خدماتنا',
    badge: 'الخدمة · تجديد وتحسين كفاءة الطاقة',
    title: 'تجديد',
    accent: 'الطاقة.',
    hero:
      'العزل الحراري وتجهيز المساحات العلوية وحلول كفاءة الطاقة لتقليل الاستهلاك ورفع قيمة المبنى على المدى الطويل.',
    home: 'الرئيسية',
    services: 'الخدمات',
    current: 'تجديد الطاقة',

    expertise: 'خبرتنا',
    expertiseTitle: 'ما نقدمه لكم',

    description:
      'ترافقكم GPE في مشاريع تجديد وتحسين كفاءة الطاقة من خلال مستشار مخصص يتابع المشروع طوال مراحله. من الدراسة الأولية إلى التنفيذ، نتولى أعمال العزل الحراري وتجهيز المساحات العلوية وحلول كفاءة الطاقة المناسبة لمبناكم، بهدف تحسين الراحة وتقليل تكاليف الطاقة.',

    methodologyTitle: 'المنهجية والمرافقة',
    methodology: [
      'الدراسات والتصميم والحسابات الفنية',
      'تدقيق الطاقة والتشخيص الأولي',
      'المرافقة والمساعدة والاستشارات من خلال مستشار مخصص',
      'متابعة المشروع من بداية الأعمال حتى الانتهاء منها',
    ],

    solutionsTitle: 'حلولنا',
    solutions: [
      'العزل الحراري الداخلي (ITI)',
      'العزل الحراري الخارجي (ITE)',
      'عزل المساحات العلوية غير المستغلة والمجهزة',
      'عزل الأرضيات السفلية',
      'تجهيز وتحويل المساحات العلوية',
      'استبدال النوافذ والأبواب وأنظمة التدفئة',
    ],

    sectorsTitle: 'قطاعات التدخل',
    sectors: [
      'السكن / المباني السكنية',
      'القطاع التجاري / المكاتب',
      'المجمعات والمباني السكنية المشتركة',
      'الهيئات المحلية / المنشآت العامة',
    ],

    ctaTitle: 'هل لديكم مشروع لتجديد المبنى؟',
    ctaText:
      'يرافقكم مستشار مخصص من مرحلة التشخيص الأولي حتى استلام الأعمال.',
    quote: 'طلب عرض سعر',
    call: 'اتصل بنا',

    relatedTitle: 'الخدمات ذات الصلة',
    related: [
      {
        slug: 'courants-forts',
        title: 'التيارات القوية',
        desc: 'التركيبات الكهربائية للمباني التجارية والصناعية.',
        icon: Zap,
      },
      {
        slug: 'energies-renouvelables',
        title: 'الطاقات المتجددة',
        desc: 'حلول الطاقة الشمسية المتكاملة.',
        icon: Sun,
      },
      {
        slug: 'mobilite-electrique',
        title: 'التنقل الكهربائي',
        desc: 'تنفيذ البنية التحتية لشحن السيارات الكهربائية.',
        icon: BatteryCharging,
      },
    ],
  },
};

const CheckItem = ({ children }) => (
  <div className="flex items-start gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 hover:border-[#0b3b8c]/30 hover:shadow-sm transition-all">
    <span className="mt-0.5 w-5 h-5 rounded-full bg-[#0b3b8c]/10 text-[#0b3b8c] flex items-center justify-center shrink-0">
      <Check className="w-3 h-3" strokeWidth={3.5} />
    </span>

    <span className="text-slate-700 text-sm">
      {children}
    </span>
  </div>
);

const SectionTitle = ({ Icon, children }) => (
  <div className="flex items-center gap-3">
    <span className="w-9 h-9 rounded-lg bg-[#0b3b8c]/10 text-[#0b3b8c] flex items-center justify-center">
      <Icon className="w-4 h-4" strokeWidth={2.4} />
    </span>

    <h3 className="text-[#0b1c3a] font-bold text-lg md:text-xl tracking-tight">
      {children}
    </h3>
  </div>
);

const RenovationEnergetique = () => {
  const { lang } = useT();

  const currentLang = ['fr', 'en', 'ar'].includes(lang)
    ? lang
    : 'fr';

  const c = content[currentLang];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <main
      className="bg-white"
      dir={currentLang === 'ar' ? 'rtl' : 'ltr'}
    >

      {/* HERO */}
      <section className="relative pt-40 pb-24 md:pt-48 md:pb-28 overflow-hidden">

        <img
          src={heroImg}
          alt={c.current}
          loading="eager"
          className="absolute inset-0 w-full h-full object-cover"
        />

        <div className="absolute inset-0 bg-gradient-to-b from-[#0b1c3a]/95 via-[#0b1c3a]/80 to-[#0b1c3a]/95" />

        <div className="absolute right-[-10%] top-1/2 -translate-y-1/2 w-[720px] h-[720px] rounded-full bg-[#0b3b8c] blur-[140px] opacity-40 pointer-events-none" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10">

          <Link
            to="/services"
            className="inline-flex items-center gap-2 text-white/80 hover:text-white text-sm font-medium"
          >
            <ArrowLeft className="w-4 h-4" />
            {c.back}
          </Link>

          <div className="mt-8 flex items-center gap-4">

            <div className="w-12 h-12 rounded-xl bg-[#0b3b8c] flex items-center justify-center border border-white/10">
              <Home
                className="w-6 h-6 text-white"
                strokeWidth={2.4}
              />
            </div>

            <span className="px-3 py-1 rounded-full bg-white/10 border border-white/20 text-white/90 text-[11px] tracking-[0.25em] font-semibold">
              {c.badge}
            </span>

          </div>

          <h1 className="mt-6 text-white font-black leading-[1.05] tracking-tight text-4xl md:text-6xl lg:text-7xl max-w-4xl">

            {c.title}{' '}

            <span className="italic font-serif text-[#f5c518]">
              {c.accent}
            </span>

          </h1>

          <p className="mt-6 text-white/85 text-base md:text-lg max-w-2xl leading-relaxed">
            {c.hero}
          </p>

          <nav
            aria-label="breadcrumb"
            className="mt-8 flex items-center gap-2 text-white/70 text-sm"
          >

            <Link
              to="/"
              className="hover:text-white transition-colors"
            >
              {c.home}
            </Link>

            <span>/</span>

            <Link
              to="/services"
              className="hover:text-white transition-colors"
            >
              {c.services}
            </Link>

            <span>/</span>

            <span className="text-[#f5c518] font-semibold">
              {c.current}
            </span>

          </nav>

        </div>

        <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-b from-transparent to-white" />

      </section>

      {/* MAIN CONTENT */}
      <section className="pb-24">

        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-[1fr_360px] gap-10 lg:gap-14 items-start">

          <div>

            <span className="inline-block px-3 py-1 rounded-full border border-slate-200 bg-slate-50 text-slate-600 text-[11px] tracking-[0.25em] font-semibold">
              {c.expertise}
            </span>

            <h2 className="mt-5 text-3xl md:text-4xl font-black text-[#0b1c3a] leading-tight tracking-tight">
              {c.expertiseTitle}
            </h2>

            <div className="mt-6 p-5 md:p-6 rounded-2xl bg-slate-50 border-l-4 border-[#0b3b8c] text-slate-700 leading-relaxed">
              {c.description}
            </div>

            {/* METHODOLOGY */}
            <div className="mt-10">

              <SectionTitle Icon={Wrench}>
                {c.methodologyTitle}
              </SectionTitle>

              <div className="mt-5 grid sm:grid-cols-2 gap-3">

                {c.methodology.map((item) => (
                  <CheckItem key={item}>
                    {item}
                  </CheckItem>
                ))}

              </div>

            </div>

            {/* SOLUTIONS */}
            <div className="mt-12">

              <SectionTitle Icon={Rocket}>
                {c.solutionsTitle}
              </SectionTitle>

              <div className="mt-5 grid sm:grid-cols-2 gap-3">

                {c.solutions.map((item) => (
                  <CheckItem key={item}>
                    {item}
                  </CheckItem>
                ))}

              </div>

            </div>

            {/* SECTORS */}
            <div className="mt-12">

              <SectionTitle Icon={Building2}>
                {c.sectorsTitle}
              </SectionTitle>

              <div className="mt-5 grid sm:grid-cols-2 gap-3">

                {c.sectors.map((item) => (
                  <CheckItem key={item}>
                    {item}
                  </CheckItem>
                ))}

              </div>

            </div>

          </div>

          {/* CTA */}
          <aside className="lg:sticky lg:top-28">

            <div className="relative bg-[#0b1c3a] text-white rounded-3xl p-7 shadow-xl overflow-hidden">

              <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-[#0b3b8c] blur-[70px] opacity-60" />

              <div className="relative">

                <div className="w-11 h-11 rounded-xl bg-[#f5c518] text-[#0b1c3a] flex items-center justify-center">
                  <Home
                    className="w-5 h-5"
                    strokeWidth={2.4}
                  />
                </div>

                <h3 className="mt-5 text-xl font-bold leading-tight">
                  {c.ctaTitle}
                </h3>

                <p className="mt-3 text-white/70 text-sm leading-relaxed">
                  {c.ctaText}
                </p>

                <Link
                  to="/contact"
                  className="mt-6 w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-[#0b3b8c] hover:bg-[#082e6f] font-semibold text-sm transition-colors"
                >
                  {c.quote}
                  <ArrowRight className="w-4 h-4" />
                </Link>

                <a
                  href="tel:+33442072262"
                  className="mt-3 w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-white text-[#0b1c3a] hover:bg-slate-100 font-semibold text-sm transition-colors"
                >
                  <Phone className="w-4 h-4" />
                  {c.call}
                </a>

              </div>

            </div>

          </aside>

        </div>

      </section>

      {/* CERTIFICATIONS */}
      <CertificationsSection />

      {/* RELATED SERVICES */}
      <section className="py-20 bg-[#f7f8fb] border-t border-slate-200">

        <div className="max-w-7xl mx-auto px-6 lg:px-10">

          <h2 className="text-2xl md:text-3xl font-black text-[#0b1c3a] tracking-tight">
            {c.relatedTitle}
          </h2>

          <div className="mt-8 grid md:grid-cols-3 gap-4">

            {c.related.map((r) => {

              const Icon = r.icon;

              return (
                <Link
                  key={r.slug}
                  to={`/services/${r.slug}`}
                  className="group bg-white rounded-2xl border border-slate-200 p-5 flex items-center gap-4 hover:shadow-md hover:border-[#0b3b8c]/30 transition-all"
                >

                  <div className="shrink-0 w-11 h-11 rounded-xl bg-[#0b3b8c]/10 text-[#0b3b8c] flex items-center justify-center group-hover:bg-[#0b3b8c] group-hover:text-white transition-colors">

                    <Icon
                      className="w-5 h-5"
                      strokeWidth={2.2}
                    />

                  </div>

                  <div className="min-w-0 flex-1">

                    <div className="text-[#0b1c3a] font-bold text-sm">
                      {r.title}
                    </div>

                    <div className="text-slate-500 text-xs mt-1 line-clamp-1">
                      {r.desc}
                    </div>

                  </div>

                  <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-[#0b3b8c] transition-colors" />

                </Link>
              );

            })}

          </div>

        </div>

      </section>

    </main>
  );
};

export default RenovationEnergetique;